/* eslint-disable no-nested-ternary */
import { useId, useMemo, useState } from 'react';
import { tv } from 'tailwind-variants';

import { ResponsiveImage } from 'components/base/image';
import { CheckSVG } from 'components/iconsSVG/check';
import { useI18n } from 'context/i18n';
import { useSunnei } from 'context/product-sheet';
import { AnimatePresence, motion } from 'motion/react';
import { interpolate } from 'utils/StringUtils';
import { CenteredBellSVG } from 'components/iconsSVG/bellCentered';

// eslint-disable-next-line react/prop-types

const popoverButton = tv({
  base: 'h-8 rounded-2xl px-5 transition-colors duration-150 ease-in-out',
  variants: {
    color: {
      red: 'bg-[var(--color-white)] text-[var(--color-error)] data-[selected="true"]:bg-[var(--color-error)] data-[selected="true"]:text-[var(--color-white)] hover:bg-[var(--color-error)] hover:text-[var(--color-white)] data-[selected="true"]:hover:bg-[var(--color-error)] data-[selected="true"]:hover:bg-opacity-70',
      default:
        'data-[selected="true"]:hover:bg-[var(--color-black)]/70 bg-[var(--color-white)] text-[var(--color-black)] data-[selected="true"]:bg-[var(--color-black)] data-[selected="true"]:text-[var(--color-white)] hover:bg-[var(--color-black)] hover:text-[var(--color-white)]',
    },
  },
});

const menColorsMap = {
  'K100996-001': '#c0332d',
  'K100996-002': '#ba7d29',
  'K100996-003': '#174a3e',
  'K100996-004': '#21374d',
  'K100996-005': '#bfb9a8',
};
const womenColorsMap = {
  'K201700-001': '#c0332d',
  'K201700-002': '#ba7d29',
  'K201700-003': '#174a3e',
  'K201700-004': '#21374d',
  'K201700-005': '#bfb9a8',
};

function getSunneiColor(id) {
  return menColorsMap[id] ?? womenColorsMap[id] ?? 'transparent';
}

const menColorsOrder = {
  'K100996-001': 2,
  'K100996-002': 3,
  'K100996-003': 0,
  'K100996-004': 4,
  'K100996-005': 1,
};
const womenColorsOrder = {
  'K201700-001': 2,
  'K201700-002': 3,
  'K201700-003': 0,
  'K201700-004': 4,
  'K201700-005': 1,
};

function getSunneiColorOrder(id) {
  return menColorsOrder[id] ?? womenColorsOrder[id] ?? 0;
}

const menColorId = {
  'K100996-001': 1,
  'K100996-002': 2,
  'K100996-003': 3,
  'K100996-004': 4,
  'K100996-005': 5,
};

const womenColorId = {
  'K201700-001': 1,
  'K201700-002': 2,
  'K201700-003': 3,
  'K201700-004': 4,
  'K201700-005': 5,
};

function getColorId(id) {
  return menColorId[id] ?? womenColorId[id] ?? 0;
}

function PickerButton({ shoeId, onSelect, sizes, picker }) {
  const { selectedSize, selectedSunneiShoes } = useSunnei();

  const selected = selectedSunneiShoes[picker === 'first' ? 0 : 1] === shoeId;

  let hasStock = true;

  if (selectedSize && sizes && sizes.length > 0) {
    const selectedSizeObject = sizes.find((size) => size.value === selectedSize);
    if (selectedSizeObject && selectedSizeObject.quantity === 0) {
      hasStock = false;
    }
    // esta condicion es por si tenemos stock a 1 y ya en el primero tenemos seleccionado el mismo color
    if (picker === 'second' && selectedSunneiShoes[0] === shoeId && selectedSizeObject && selectedSizeObject.quantity === 1) {
      hasStock = false;
    }
  }

  return (
    <button
      className="flex size-8 items-center justify-center rounded-full border-2 border-[var(--color-white)]"
      style={{ backgroundColor: getSunneiColor(shoeId) }}
      onClick={onSelect}
    >
      <span className="sr-only">color {shoeId}</span>
      {selected ?
        <CheckSVG className="text-[var(--color-white)]" />
      : null}
      {!selected && !hasStock ?
        <CenteredBellSVG className="size-5 text-[var(--color-white)]" />
      : null}
    </button>
  );
}

export function SunneiPicker() {
  const { t } = useI18n();
  const id = useId();
  const { sunneiInfo, selectedSunneiShoes, setSelectedSunneiShoes, inspirationImages, inspirationId, firstProductHasStock, secondProductHasStock } =
    useSunnei();
  const [open, setOpen] = useState([true, true]);
  const [mobileOpen, setMobileOpen] = useState([true, false]);

  const allImages = sunneiInfo.all
    .sort((a, b) => getSunneiColorOrder(a) - getSunneiColorOrder(b))
    .reduce((acc, shoeId) => {
      const im = sunneiInfo?.products?.[shoeId]?.images.find((imageObject) =>
        Object.values(imageObject).some((value) => typeof value === 'string' && value.endsWith('CF.jpg')),
      );
      if (im) {
        acc.set(shoeId, { ...im, shoeId });
      }
      return acc;
    }, new Map());

  const firstImage = allImages.get(selectedSunneiShoes[0]);
  const secondImage = allImages.get(selectedSunneiShoes[1]);
  // an inspiration id is K100996-999-S011. I need to replace the last two characters a combination if the values in the
  const replacedId = inspirationId.replace(/-S0\d{2}$/, `-S0${getColorId(selectedSunneiShoes[0])}${getColorId(selectedSunneiShoes[1])}`);
  // replace last element of array with own string
  const combinedImage = `${inspirationImages.md.replace(inspirationId, replacedId).split('_').slice(0, -1).join('_')}_C.jpg`;

  const productSizes = useMemo(() => {
    const allProductSizes = Object.entries(sunneiInfo.products).reduce((acc, [shoeId, entrySheet]) => {
      const sizes = entrySheet.sizes ?? [];

      if (Array.isArray(sizes)) {
        acc[shoeId] = sizes;
      }
      return acc;
    }, {});
    return allProductSizes;
  }, [sunneiInfo.products]);

  const noStock = Object.values(productSizes).every((sizes) => sizes.length === 0);

  return (
    <div className="relative grid w-full grid-cols-1 lg:grid-cols-2">
      <div className="hidden">
        {Array.from(allImages).map(([shoeId, image]) => (
          <ResponsiveImage
            key={`${id}-hidden-image-${shoeId}`}
            src={image.md}
            sources={[
              { source: image.md.replace(/\.jpg|\.png/, '.avif'), type: 'image/avif' },
              { source: image.md.replace(/\.jpg|\.png/, '.webp'), type: 'image/webp' },
            ]}
            alt=""
          />
          // <img key={`${id}-hidden-image-${shoeId}`} src={image.md} alt={image.altText} />
        ))}
      </div>
      {/* Mobile */}
      <div className="col-start-1 row-start-1 h-full w-full lg:hidden">
        <ResponsiveImage
          src={combinedImage}
          sources={[
            { source: combinedImage.replace(/\.jpg|\.png/, '.avif'), type: 'image/avif' },
            { source: combinedImage.replace(/\.jpg|\.png/, '.webp'), type: 'image/webp' },
          ]}
          alt=""
          className="h-full w-full object-contain"
        />
        {/* <img src={combinedImage.md} alt={combinedImage.altText} className='object-contain w-full h-full' /> */}
      </div>

      <div className="col-start-1 row-start-1 mx-auto mb-auto mt-3 flex w-full flex-col items-center justify-center gap-4 lg:hidden">
        <div className="flex justify-center gap-6">
          <button
            data-selected={mobileOpen[0] === true}
            onClick={() => setMobileOpen((prev) => [!prev[0], false])}
            className={popoverButton({ color: !firstProductHasStock ? 'red' : 'default' })}
          >
            {!firstProductHasStock ?
              t('listado.productos', 'producto.agotado')
            : interpolate(t('ficha.producto', 'shoe.number', 'Zapato <TAG number>'), { '<TAG number>': '1' })}
          </button>
          <button
            data-selected={mobileOpen[1] === true}
            onClick={() => setMobileOpen((prev) => [false, !prev[1]])}
            className={popoverButton({ color: !secondProductHasStock ? 'red' : 'default' })}
          >
            {!secondProductHasStock ?
              t('listado.productos', 'producto.agotado')
            : interpolate(t('ficha.producto', 'shoe.number', 'Zapato <TAG number>'), { '<TAG number>': '2' })}
          </button>
        </div>
        <AnimatePresence initial={false} mode="wait">
          {mobileOpen[0] ?
            <motion.div
              key="firstImageButtons"
              initial={{ opacity: 0, y: -10 }}
              animate={{
                opacity: 1,
                y: 0,
                transition: {
                  motion: 'tween',
                  duration: 0.15,
                },
              }}
              exit={{ opacity: 0, y: -10 }}
              className="flex items-center justify-center gap-2"
            >
              {sunneiInfo.all
                .sort((a, b) => getSunneiColorOrder(a) - getSunneiColorOrder(b))
                .map((shoeId) => (
                  <PickerButton
                    key={`${id}-first-color-${shoeId}`}
                    picker="first"
                    shoeId={shoeId}
                    onSelect={() => setSelectedSunneiShoes((prev) => [shoeId, prev[1]])}
                    sizes={productSizes[shoeId]}
                  />
                ))}
            </motion.div>
          : null}
          {mobileOpen[1] ?
            <motion.div
              key="secondImageButtons"
              initial={{ opacity: 0, y: -10 }}
              animate={{
                opacity: 1,
                y: 0,
                transition: {
                  motion: 'tween',
                  duration: 0.15,
                },
              }}
              exit={{ opacity: 0, y: -10 }}
              className="flex items-center justify-center gap-2"
            >
              {sunneiInfo.all
                .sort((a, b) => getSunneiColorOrder(a) - getSunneiColorOrder(b))
                .map((shoeId) => (
                  <PickerButton
                    key={`${id}-second-color-${shoeId}`}
                    picker="second"
                    shoeId={shoeId}
                    onSelect={() => setSelectedSunneiShoes((prev) => [prev[0], shoeId])}
                    sizes={productSizes[shoeId]}
                  />
                ))}
            </motion.div>
          : null}
        </AnimatePresence>
      </div>

      {/* Desktop */}
      {firstImage ?
        <div className="col-start-1 row-start-1 h-full w-full max-lg:hidden">
          <ResponsiveImage
            src={firstImage.md}
            sources={[
              { source: firstImage.md.replace(/\.jpg|\.png/, '.avif'), type: 'image/avif' },
              { source: firstImage.md.replace(/\.jpg|\.png/, '.webp'), type: 'image/webp' },
            ]}
            alt=""
            className="h-full w-full object-cover"
          />
          {/* <img src={firstImage.md} alt={firstImage.altText} className='object-contain w-full h-full' /> */}
        </div>
      : null}
      {secondImage ?
        <div className="col-start-2 row-start-1 h-full w-full max-lg:hidden">
          <ResponsiveImage
            src={secondImage.md}
            sources={[
              { source: secondImage.md.replace(/\.jpg|\.png/, '.avif'), type: 'image/avif' },
              { source: secondImage.md.replace(/\.jpg|\.png/, '.webp'), type: 'image/webp' },
            ]}
            alt=""
            className="h-full w-full object-cover"
          />
          {/* <img src={secondImage.md} alt={secondImage.altText} className='object-contain w-full h-full' /> */}
        </div>
      : null}
      <div className="col-start-1 row-start-1 mx-auto mb-auto mt-9 flex flex-col items-center justify-center gap-4 max-lg:hidden">
        <button
          data-selected={open[0] === true}
          onClick={() => setOpen((prev) => [!prev[0], prev[1]])}
          className={popoverButton({ color: !firstProductHasStock ? 'red' : 'default' })}
        >
          {!firstProductHasStock ?
            t('listado.productos', 'producto.agotado')
          : interpolate(t('ficha.producto', 'shoe.number', 'Zapato <TAG number>'), { '<TAG number>': '1' })}
        </button>
        <AnimatePresence initial={false} mode="wait">
          {open[0] === true ?
            <motion.div
              key="desktopFirstImageButtons"
              initial={{ opacity: 0, y: -10 }}
              animate={{
                opacity: 1,
                y: 0,
                transition: {
                  motion: 'tween',
                  duration: 0.15,
                },
              }}
              exit={{ opacity: 0, y: -10 }}
              className="flex items-center justify-center gap-2"
            >
              {sunneiInfo.all
                .sort((a, b) => getSunneiColorOrder(a) - getSunneiColorOrder(b))
                .map((shoeId) => (
                  <PickerButton
                    key={`${id}-first-color-${shoeId}`}
                    picker="first"
                    shoeId={shoeId}
                    onSelect={() => setSelectedSunneiShoes((prev) => [shoeId, prev[1]])}
                    sizes={productSizes[shoeId]}
                  />
                ))}
            </motion.div>
          : null}
        </AnimatePresence>
      </div>
      <div className="col-start-2 row-start-1 mx-auto mb-auto mt-9 flex flex-col items-center justify-center gap-4 max-lg:hidden">
        <button
          data-selected={open[1] === true}
          data-stock={noStock}
          onClick={() => setOpen((prev) => [prev[0], !prev[1]])}
          className={popoverButton({ color: !secondProductHasStock ? 'red' : 'default' })}
        >
          {!secondProductHasStock ?
            t('listado.productos', 'producto.agotado')
          : interpolate(t('ficha.producto', 'shoe.number', 'Zapato <TAG number>'), { '<TAG number>': '2' })}
        </button>
        <AnimatePresence initial={false} mode="wait">
          {open[1] === true ?
            <motion.div
              key="desktopSecondImageButtons"
              initial={{ opacity: 0, y: -10 }}
              animate={{
                opacity: 1,
                y: 0,
                transition: {
                  motion: 'tween',
                  duration: 0.15,
                },
              }}
              exit={{ opacity: 0, y: -10 }}
              className="flex items-center justify-center gap-2"
            >
              {sunneiInfo.all
                .sort((a, b) => getSunneiColorOrder(a) - getSunneiColorOrder(b))
                .map((shoeId) => (
                  <PickerButton
                    key={`${id}-second-color-${shoeId}`}
                    picker="second"
                    shoeId={shoeId}
                    onSelect={() => setSelectedSunneiShoes((prev) => [prev[0], shoeId])}
                    sizes={productSizes[shoeId]}
                  />
                ))}
            </motion.div>
          : null}
        </AnimatePresence>
      </div>
    </div>
  );
}
